import { useState, useRef, useEffect, useCallback } from 'react';
import {
  FileSignature, Send, Loader2, Plus, Trash2, X, Search,
  AlertTriangle, Stethoscope, Pill, ClipboardList, Heart,
  Calendar, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import './PrescriptionForm.css';

// ── FHIR ICD-10 Typeahead Hook ──────────────────────────────────────────────
function useIcdSearch() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const search = useCallback((query) => {
    clearTimeout(timerRef.current);
    if (!query || query.length < 2) { setResults([]); return; }

    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=${encodeURIComponent(query)}&maxList=12`
        );
        const data = await res.json();
        // Response format: [totalCount, codes[], null, displayStrings[][code, name]]
        const items = (data[3] || []).map((entry) => ({
          code: entry[0],
          label: entry[1],
        }));
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  const clear = useCallback(() => { setResults([]); setLoading(false); }, []);
  return { results, loading, search, clear };
}

// ── Empty medication row factory ────────────────────────────────────────────
const emptyMedRow = () => ({
  id: crypto.randomUUID(),
  drug_name: '',
  dose: '',
  frequency: '',
  duration: '',
  route: 'Oral',
});

const FREQUENCY_OPTIONS = [
  'OD / Once a day',
  'BD / Twice a day',
  'TDS / Thrice a day',
  'QID / Four times a day',
  'PRN / As needed',
  'SOS',
  'Stat',
  'HS / At bedtime',
];

const ROUTE_OPTIONS = ['Oral', 'Intravenous', 'Intramuscular', 'Subcutaneous', 'Topical', 'Inhalation', 'Sublingual', 'Rectal', 'Ophthalmic', 'Otic'];

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════
export function PrescriptionForm({ session, caseData }) {
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(!!session?.prescription);
  const [errors, setErrors] = useState({});

  // ── Section A: Clinical Baseline ──
  const [chiefComplaint, setChiefComplaint] = useState(
    session?.prescription?.chief_complaint || caseData?.chief_complaint || ''
  );
  const [historySummary, setHistorySummary] = useState(
    session?.prescription?.history_summary || ''
  );

  // ── Section B: Diagnostic Coding ──
  const [provisionalDx, setProvisionalDx] = useState(
    session?.prescription?.provisional_diagnosis
      ? [session.prescription.provisional_diagnosis]
      : []
  );
  const [differentialDx, setDifferentialDx] = useState(
    session?.prescription?.differential_diagnoses || []
  );
  const [provQuery, setProvQuery] = useState('');
  const [diffQuery, setDiffQuery] = useState('');
  const provSearch = useIcdSearch();
  const diffSearch = useIcdSearch();
  const provRef = useRef(null);
  const diffRef = useRef(null);

  // ── Section C: Medication Matrix ──
  const [medications, setMedications] = useState(
    session?.prescription?.medications?.length
      ? session.prescription.medications.map((m) => ({ ...m, id: crypto.randomUUID() }))
      : [emptyMedRow()]
  );
  const [noMedications, setNoMedications] = useState(
    session?.prescription?.no_medications || false
  );

  // ── Section D: Orders & Advice ──
  const [investigations, setInvestigations] = useState(
    session?.prescription?.investigations || ''
  );
  const [lifestyleAdvice, setLifestyleAdvice] = useState(
    session?.prescription?.lifestyle_advice || ''
  );
  const [additionalNotes, setAdditionalNotes] = useState(
    session?.prescription?.additional_notes || ''
  );

  // ── Section E: Follow-up ──
  const [followUpDays, setFollowUpDays] = useState(
    session?.prescription?.follow_up_days ?? ''
  );

  // ── Close dropdowns on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (provRef.current && !provRef.current.contains(e.target)) provSearch.clear();
      if (diffRef.current && !diffRef.current.contains(e.target)) diffSearch.clear();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [provSearch, diffSearch]);

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!chiefComplaint.trim()) e.chiefComplaint = 'Chief complaint is required.';
    if (historySummary.trim().length < 30) e.historySummary = 'Minimum 30 characters required.';
    if (provisionalDx.length === 0) e.provisionalDx = 'At least one provisional diagnosis is required.';
    if (!noMedications && medications.every((m) => !m.drug_name.trim())) {
      e.medications = 'Add at least one medication or check "No medications".';
    }
    if (followUpDays === '' || followUpDays === null || followUpDays === undefined) {
      e.followUpDays = 'Follow-up is required. Enter 0 for immediate referral.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Medication helpers ──
  const addMedRow = () => setMedications((prev) => [...prev, emptyMedRow()]);
  const removeMedRow = (id) => setMedications((prev) => prev.filter((r) => r.id !== id));
  const updateMedRow = (id, field, value) =>
    setMedications((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitted) return;
    
    if (!validate()) {
      toast.error('Please complete all required fields (marked with *).');
      return;
    }

    const payload = {
      chief_complaint: chiefComplaint.trim(),
      history_summary: historySummary.trim(),
      provisional_diagnosis_code: provisionalDx[0]?.code || '',
      provisional_diagnosis_label: provisionalDx[0]?.label || '',
      differential_diagnoses: differentialDx.map(d => `${d.code} - ${d.label}`),
      no_medications: noMedications,
      medications: noMedications
        ? []
        : medications
            .filter((m) => m.drug_name.trim())
            .map(({ id, drug_name, ...rest }) => ({
               drug: drug_name.trim(),
               ...rest
            })),
      investigations_ordered: investigations.trim() ? investigations.trim().split('\n').filter(s => s.trim()) : [],
      lifestyle_advice: lifestyleAdvice.trim() ? lifestyleAdvice.trim().split('\n').filter(s => s.trim()) : [],
      additional_notes: additionalNotes.trim(),
      follow_up_days: Number(followUpDays) || 0,
    };

    try {
      setSubmitting(true);
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}/prescription`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      toast.success('Prescription submitted — evaluation pipeline started.');
      setTimeout(() => {
        window.location.href = '/cs/scorecard';
      }, 1500);
    } catch (err) {
      console.error('Prescription submit failed', err);
      toast.error('Failed to submit prescription.');
    } finally {
      setSubmitting(false);
    }
  };

  // ═════════════════════════════════════════════════════════════════════════
  //  POST-SUBMIT VIEW
  // ═════════════════════════════════════════════════════════════════════════
  if (submitted) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50">
        <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mb-6">
          <FileSignature className="w-10 h-10 text-violet-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Prescription Submitted</h2>
        <p className="text-slate-500 max-w-md mb-8">
          Your evaluation is being generated. You can view your detailed scorecard in the scorecard tab.
        </p>
        <button
          onClick={() => (window.location.href = '/cs/scorecard')}
          className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold shadow-md transition-all"
        >
          View Scorecard
        </button>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  //  FORM VIEW
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="flex-1 flex flex-col h-full bg-white relative">
      {/* ── Locked overlay while submitting ── */}
      {submitting && (
        <div className="rx-locked-overlay">
          <div className="rx-spinner mb-4" />
          <p className="text-sm font-semibold text-slate-600">Locking form & starting evaluation…</p>
        </div>
      )}

      {/* ── Header ── */}
      <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center gap-4">
        <div className="w-12 h-12 bg-violet-100 text-violet-600 rounded-xl flex items-center justify-center shadow-sm">
          <FileSignature className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">Post-Call Prescription</h2>
          <p className="text-sm text-slate-500">
            Complete findings for <span className="font-semibold text-slate-700">{caseData?.patient?.name}</span>
          </p>
        </div>
      </div>

      {/* ── Scrollable Form Body ── */}
      <div className="flex-1 overflow-y-auto p-8">
        <form id="prescription-form" onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-10">

          {/* ═══ SECTION A — Clinical Baseline & Narrative ═══ */}
          <section>
            <div className="rx-section-header">
              <span className="rx-badge bg-violet-500">A</span>
              <h3>Clinical Baseline &amp; Narrative</h3>
            </div>

            {/* Chief Complaint */}
            <div className="space-y-1.5 mb-5">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <Stethoscope className="w-3.5 h-3.5 text-violet-500" />
                Chief Complaint <span className="text-red-400">*</span>
              </label>
              <textarea
                id="rx-chief-complaint"
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                rows={2}
                placeholder="Primary reason for the patient's visit"
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all shadow-sm resize-none"
              />
              {chiefComplaint && (
                <p className="rx-field-hint">Pre-populated from transcript — editable</p>
              )}
              {errors.chiefComplaint && <p className="rx-field-error">{errors.chiefComplaint}</p>}
            </div>

            {/* History Summary */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-slate-700">
                History Summary <span className="text-red-400">*</span>
              </label>
              <textarea
                id="rx-history-summary"
                value={historySummary}
                onChange={(e) => setHistorySummary(e.target.value)}
                rows={4}
                placeholder="Document your anamnesis notes… (min 30 characters)"
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all shadow-sm resize-none"
              />
              <div className="flex items-center justify-between">
                <p className={`text-xs ${historySummary.length >= 30 ? 'text-violet-500' : 'text-slate-400'}`}>
                  {historySummary.length}/30 min characters
                </p>
                {errors.historySummary && <p className="rx-field-error">{errors.historySummary}</p>}
              </div>
            </div>
          </section>

          {/* ═══ SECTION B — Diagnostic Coding ═══ */}
          <section>
            <div className="rx-section-header">
              <span className="rx-badge bg-blue-500">B</span>
              <h3>Diagnostic Coding</h3>
            </div>

            {/* Provisional Diagnosis — ICD Typeahead */}
            <div className="space-y-1.5 mb-5">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5 text-blue-500" />
                Provisional Diagnosis <span className="text-red-400">*</span>
              </label>

              {provisionalDx.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-2">
                  {provisionalDx.map((dx, i) => (
                    <span key={i} className="dx-chip bg-violet-50 text-violet-800 border border-violet-200">
                      <span className="font-mono text-[11px] font-bold text-violet-600">{dx.code}</span>
                      <span className="text-xs">{dx.label}</span>
                      <button type="button" className="dx-chip-remove" onClick={() => setProvisionalDx([])}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="icd-typeahead-wrapper" ref={provRef}>
                  <input
                    id="rx-prov-dx-search"
                    type="text"
                    value={provQuery}
                    onChange={(e) => { setProvQuery(e.target.value); provSearch.search(e.target.value); }}
                    placeholder="Search ICD-10 codes (e.g. J06.9 or fever)…"
                    className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    autoComplete="off"
                  />
                  {(provSearch.results.length > 0 || provSearch.loading) && (
                    <div className="icd-dropdown">
                      {provSearch.loading ? (
                        <div className="icd-dropdown-loading">
                          <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                        </div>
                      ) : (
                        provSearch.results.map((item, idx) => (
                          <div
                            key={idx}
                            className="icd-dropdown-item"
                            onClick={() => {
                              setProvisionalDx([{ code: item.code, label: item.label }]);
                              setProvQuery('');
                              provSearch.clear();
                            }}
                          >
                            <span className="icd-code">{item.code}</span>
                            <span className="icd-label">{item.label}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.provisionalDx && <p className="rx-field-error">{errors.provisionalDx}</p>}
            </div>

            {/* Differential Diagnoses — chips (max 3) */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-slate-700">
                Differential Diagnoses <span className="text-slate-400 font-normal">(optional, max 3)</span>
              </label>

              <div className="flex flex-wrap gap-2 mb-2">
                {differentialDx.map((dx, i) => (
                  <span key={i} className="dx-chip bg-blue-50 text-blue-800 border border-blue-200">
                    <span className="font-mono text-[11px] font-bold text-blue-600">{dx.code}</span>
                    <span className="text-xs">{dx.label}</span>
                    <button
                      type="button"
                      className="dx-chip-remove"
                      onClick={() => setDifferentialDx((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {differentialDx.length < 3 && (
                <div className="icd-typeahead-wrapper" ref={diffRef}>
                  <input
                    id="rx-diff-dx-search"
                    type="text"
                    value={diffQuery}
                    onChange={(e) => { setDiffQuery(e.target.value); diffSearch.search(e.target.value); }}
                    placeholder="Add differential diagnosis…"
                    className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    autoComplete="off"
                  />
                  {(diffSearch.results.length > 0 || diffSearch.loading) && (
                    <div className="icd-dropdown">
                      {diffSearch.loading ? (
                        <div className="icd-dropdown-loading">
                          <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                        </div>
                      ) : (
                        diffSearch.results.map((item, idx) => (
                          <div
                            key={idx}
                            className="icd-dropdown-item"
                            onClick={() => {
                              if (differentialDx.length >= 3) return;
                              setDifferentialDx((prev) => [...prev, { code: item.code, label: item.label }]);
                              setDiffQuery('');
                              diffSearch.clear();
                            }}
                          >
                            <span className="icd-code">{item.code}</span>
                            <span className="icd-label">{item.label}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ═══ SECTION C — Medication Matrix ═══ */}
          <section>
            <div className="rx-section-header">
              <span className="rx-badge bg-amber-500">C</span>
              <h3>Medication Matrix</h3>
            </div>

            {/* No-Medications Toggle */}
            <label className={`no-med-toggle mb-4 ${noMedications ? 'active' : ''}`}>
              <input
                id="rx-no-medications"
                type="checkbox"
                checked={noMedications}
                onChange={(e) => setNoMedications(e.target.checked)}
              />
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <span>No medications — triage / referral bypass</span>
            </label>

            {/* Grid */}
            <div className={`med-grid ${noMedications ? 'med-grid-disabled' : ''}`}>
              <div className="med-grid-head">
                <span>Drug Name</span>
                <span>Dose</span>
                <span>Frequency</span>
                <span>Duration</span>
                <span>Route</span>
                <span></span>
              </div>

              {medications.map((row) => (
                <div className="med-grid-row" key={row.id}>
                  <input
                    type="text"
                    value={row.drug_name}
                    onChange={(e) => updateMedRow(row.id, 'drug_name', e.target.value)}
                    placeholder="Drug name"
                    disabled={noMedications}
                  />
                  <input
                    type="text"
                    value={row.dose}
                    onChange={(e) => updateMedRow(row.id, 'dose', e.target.value)}
                    placeholder="e.g. 500mg"
                    disabled={noMedications}
                  />
                  <select
                    value={row.frequency}
                    onChange={(e) => updateMedRow(row.id, 'frequency', e.target.value)}
                    disabled={noMedications}
                  >
                    <option value="">Select…</option>
                    {FREQUENCY_OPTIONS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={row.duration}
                    onChange={(e) => updateMedRow(row.id, 'duration', e.target.value)}
                    placeholder="e.g. 5 days"
                    disabled={noMedications}
                  />
                  <select
                    value={row.route}
                    onChange={(e) => updateMedRow(row.id, 'route', e.target.value)}
                    disabled={noMedications}
                  >
                    {ROUTE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="med-delete-btn"
                    onClick={() => removeMedRow(row.id)}
                    disabled={noMedications || medications.length <= 1}
                    title="Remove row"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add row button */}
            {!noMedications && (
              <button
                type="button"
                onClick={addMedRow}
                className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg border border-violet-200 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Medication Row
              </button>
            )}
            {errors.medications && <p className="rx-field-error mt-2">{errors.medications}</p>}
          </section>

          {/* ═══ SECTION D — Orders & Non-Pharma Instructions ═══ */}
          <section>
            <div className="rx-section-header">
              <span className="rx-badge bg-violet-500">D</span>
              <h3>Orders &amp; Advice</h3>
            </div>

            <div className="space-y-5">
              {/* Investigations */}
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <ClipboardList className="w-3.5 h-3.5 text-violet-500" />
                  Investigations <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="rx-investigations"
                  value={investigations}
                  onChange={(e) => setInvestigations(e.target.value)}
                  rows={3}
                  placeholder={"One per line:\nComplete Blood Count\nHbA1c\nChest X-Ray"}
                  className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all shadow-sm resize-none font-mono"
                />
              </div>

              {/* Lifestyle Advice */}
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-red-400" />
                  Lifestyle / Non-Pharma Advice <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="rx-lifestyle-advice"
                  value={lifestyleAdvice}
                  onChange={(e) => setLifestyleAdvice(e.target.value)}
                  rows={3}
                  placeholder="Diet guidelines, rest periods, activity restrictions…"
                  className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all shadow-sm resize-none"
                />
              </div>

              {/* Additional Notes */}
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-slate-700">
                  Additional Notes <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="rx-additional-notes"
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  rows={3}
                  placeholder="Supplementary context or clinical justifications…"
                  className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all shadow-sm resize-none"
                />
              </div>
            </div>
          </section>

          {/* ═══ SECTION E — Continuity of Care ═══ */}
          <section>
            <div className="rx-section-header">
              <span className="rx-badge bg-cyan-500">E</span>
              <h3>Continuity of Care</h3>
            </div>

            <div className="space-y-1.5 max-w-xs">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-500" />
                Follow-Up In (days) <span className="text-red-400">*</span>
              </label>
              <input
                id="rx-follow-up-days"
                type="number"
                min="0"
                value={followUpDays}
                onChange={(e) => setFollowUpDays(e.target.value)}
                placeholder="e.g. 7"
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all shadow-sm"
              />
              <p className="rx-field-hint">Enter 0 to flag immediate referral / escalation.</p>
              {errors.followUpDays && <p className="rx-field-error">{errors.followUpDays}</p>}
            </div>
          </section>
        </form>
      </div>

      {/* ── Sticky Submit Bar ── */}
      <div className="rx-submit-bar">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <CheckCircle2 className="w-4 h-4 text-violet-400" />
          <span>All required fields must be completed before submission</span>
        </div>
        <button
          form="prescription-form"
          type="submit"
          disabled={submitting}
          className="rx-submit-btn"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          Submit Prescription
        </button>
      </div>
    </div>
  );
}
